
import { Canvas } from "fabric";
import { SampleImagesDialog } from "@/components/SampleImagesDialog";
import { LayersSidebar } from "@/components/LayersSidebar";
import { DrawingHistorySheet } from "@/components/drawing-history/DrawingHistorySheet";
import { SlidingPricingPanel } from "@/components/pricing/SlidingPricingPanel";
import { useRef, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLayerManagement } from "@/hooks/use-layer-management";
import { CustomCanvas } from "@/types/fabric";

interface StudioDialogsProps {
  canvas: CustomCanvas | null;
  showSampleImages: boolean;
  showLayersSidebar: boolean;
  showDrawingHistory: boolean;
  showPricing: boolean;
  onSampleImagesClose: () => void;
  onLayersSidebarClose: () => void;
  onDrawingHistoryChange: (open: boolean) => void;
  onPricingClose: () => void;
  onToolChange: (tool: string) => void;
  onLoadDrawing: (drawingData: string, drawingId: string, analysisData?: any) => void;
  onRefreshDrawings: (fn: () => void) => void;
  onGetStarted: () => void;
  onResetAnalysis?: (resetFn: () => void) => void;
  // New props for layer management
  layerManagement?: ReturnType<typeof useLayerManagement>;
}

export const StudioDialogs = ({
  canvas,
  showSampleImages,
  showLayersSidebar,
  showDrawingHistory,
  showPricing,
  onSampleImagesClose,
  onLayersSidebarClose,
  onDrawingHistoryChange,
  onPricingClose,
  onToolChange,
  onLoadDrawing,
  onRefreshDrawings,
  onGetStarted,
  onResetAnalysis,
  layerManagement,
}: StudioDialogsProps) => {
  // Store resetAnalysis function from child components
  const resetAnalysisRef = useRef<(() => void) | null>(null);
  const isMobile = useIsMobile();
  
  // Helper function that will be called by child components
  const handleResetAnalysis = (resetFn: () => void) => {
    resetAnalysisRef.current = resetFn;
    if (onResetAnalysis) {
      onResetAnalysis(resetFn);
    }
  };

  return (
    <>
      <SampleImagesDialog 
        isOpen={showSampleImages} 
        onClose={onSampleImagesClose}
        canvas={canvas}
        onToolChange={onToolChange}
        onResetAnalysis={resetAnalysisRef.current}
      />
      <LayersSidebar
        isOpen={showLayersSidebar}
        onClose={onLayersSidebarClose}
        canvas={canvas}
        onToolChange={onToolChange}
        onResetAnalysis={resetAnalysisRef.current}
        // Pass down all layer management props consistently
        layers={layerManagement?.layers || []}
        toggleLayerVisibility={layerManagement?.toggleLayerVisibility || (() => {})}
        renameLayer={layerManagement?.renameLayer || (() => {})}
        bringLayerForward={layerManagement?.bringLayerForward || (() => {})}
        sendLayerBackward={layerManagement?.sendLayerBackward || (() => {})}
      />
      <DrawingHistorySheet 
        open={showDrawingHistory}
        onOpenChange={onDrawingHistoryChange}
        onLoadDrawing={onLoadDrawing}
        onRefreshNeeded={onRefreshDrawings}
        canvas={canvas}
      />
      <SlidingPricingPanel 
        isOpen={showPricing}
        onClose={onPricingClose}
        onGetStarted={onGetStarted}
      />
    </>
  );
};
